import React from "react";
import {
  Box,
  Button,
  Collapse,
  Container,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import VisibilityOffRoundedIcon from "@mui/icons-material/VisibilityOffRounded";
import logo from "../../../images/LogoNavbar1.svg";

export const AUTH_BRAND = Object.freeze({
  orange: "#F35A3A",
  dark: "#19242F",
});

export const authPrimaryButtonSx = {
  minHeight: 48,
  bgcolor: AUTH_BRAND.orange,
  borderRadius: 999,
  textTransform: "none",
  fontWeight: 900,
  px: 2.5,
  "&:hover": { bgcolor: "#dc4d31" },
};

export const authSecondaryButtonSx = {
  minHeight: 44,
  borderRadius: 999,
  textTransform: "none",
  fontWeight: 850,
  color: AUTH_BRAND.dark,
};

export const authLinkSx = {
  color: AUTH_BRAND.orange,
  fontWeight: 900,
};

export function AuthPageShell({
  title,
  subtitle,
  icon,
  help,
  helpLabel = "Más información",
  children,
  footer,
}) {
  const [helpOpen, setHelpOpen] = React.useState(false);

  return (
    <Box
      sx={{
        minHeight: "100dvh",
        bgcolor: "#F6F7FB",
        display: "flex",
        alignItems: { xs: "flex-start", sm: "center" },
        py: { xs: 1.5, sm: 4 },
      }}
    >
      <Container maxWidth="xs">
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3.5 },
            borderRadius: { xs: 3, sm: 4 },
            border: "1px solid rgba(25,36,47,0.08)",
            boxShadow: { xs: "none", sm: "0 18px 50px rgba(25,36,47,.06)" },
          }}
        >
          <Stack spacing={{ xs: 1.75, sm: 2.25 }}>
            <Stack alignItems="center" spacing={{ xs: 1, sm: 1.25 }}>
              <Box
                component="img"
                src={logo}
                alt="WebProfit"
                sx={{ width: { xs: 44, sm: 56 }, height: { xs: 44, sm: 56 }, objectFit: "contain" }}
              />

              {icon ? (
                <Box
                  aria-hidden="true"
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: "50%",
                    display: { xs: "none", sm: "grid" },
                    placeItems: "center",
                    bgcolor: "rgba(243,90,58,0.1)",
                    color: AUTH_BRAND.orange,
                  }}
                >
                  {icon}
                </Box>
              ) : null}

              <Box sx={{ textAlign: "center" }}>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 950,
                    color: AUTH_BRAND.dark,
                    fontSize: { xs: "1.35rem", sm: "1.75rem" },
                    lineHeight: 1.15,
                  }}
                >
                  {title}
                </Typography>
                {subtitle ? (
                  <Typography
                    sx={{
                      color: "rgba(25,36,47,0.66)",
                      mt: 0.5,
                      fontSize: { xs: ".82rem", sm: ".9rem" },
                      lineHeight: 1.45,
                    }}
                  >
                    {subtitle}
                  </Typography>
                ) : null}
              </Box>
            </Stack>

            {children}

            {help ? (
              <Box>
                <Button
                  type="button"
                  size="small"
                  startIcon={<HelpOutlineRoundedIcon fontSize="small" />}
                  endIcon={
                    <KeyboardArrowDownRoundedIcon
                      fontSize="small"
                      sx={{ transform: helpOpen ? "rotate(180deg)" : "none", transition: "transform .18s ease" }}
                    />
                  }
                  onClick={() => setHelpOpen((value) => !value)}
                  aria-expanded={helpOpen}
                  sx={{
                    minHeight: 44,
                    width: "100%",
                    justifyContent: "center",
                    color: "rgba(25,36,47,.7)",
                    textTransform: "none",
                    fontWeight: 800,
                    fontSize: ".78rem",
                  }}
                >
                  {helpLabel}
                </Button>
                <Collapse in={helpOpen} unmountOnExit>
                  <Box
                    sx={{
                      mt: 0.5,
                      p: 1.25,
                      borderRadius: 2.5,
                      bgcolor: "rgba(25,36,47,.035)",
                      color: "rgba(25,36,47,.7)",
                      fontSize: ".78rem",
                      lineHeight: 1.5,
                    }}
                  >
                    {help}
                  </Box>
                </Collapse>
              </Box>
            ) : null}

            {footer ? (
              <Box sx={{ textAlign: "center", color: "rgba(25,36,47,0.7)", fontSize: ".85rem" }}>
                {footer}
              </Box>
            ) : null}
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
}

export function AuthPasswordField({
  label,
  name,
  value,
  onChange,
  autoComplete = "new-password",
  helperText,
  disabled = false,
  required = true,
  inputProps,
  autoFocus = false,
}) {
  const [visible, setVisible] = React.useState(false);

  return (
    <TextField
      label={label}
      name={name}
      type={visible ? "text" : "password"}
      value={value}
      onChange={onChange}
      required={required}
      disabled={disabled}
      fullWidth
      autoFocus={autoFocus}
      autoComplete={autoComplete}
      helperText={helperText}
      inputProps={{
        minLength: 8,
        maxLength: 128,
        autoCapitalize: "none",
        spellCheck: "false",
        ...inputProps,
      }}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <IconButton
              type="button"
              aria-label={visible ? "Ocultar contraseña" : "Mostrar contraseña"}
              onClick={() => setVisible((current) => !current)}
              edge="end"
            >
              {visible ? <VisibilityOffRoundedIcon /> : <VisibilityRoundedIcon />}
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  );
}
